import { useState } from 'react';
import { Alert, Table } from 'react-bootstrap';
import { IOpoCase, IOpoCaseRequest, OpoCaseStatus, OpoCaseStatusDescription } from '@nkr/models/opo-case';
import useOpoCases from '@nkr/store/opo/hooks/use-opo-cases';
import { exportToXlsx } from '@nkr/utils/exportToExcel';
import { formatDateTime } from './helpers';
import { CaseTableCard } from './styles';
import CaseFilter from './case-filter';
import CaseRow from './case-row';
import CaseEditModal, { ICaseClosing } from './case-edit-modal';
import CaseCloseModal from './case-close-modal';
import CaseDeleteModal from './case-delete-modal';

const exportColumns = [
    { key: 'caseNumber', header: 'Case #' },
    { key: 'unosId', header: 'UNOS ID' },
    { key: 'originCenter', header: 'Origin Center' },
    { key: 'intendedCenter', header: 'Intended Center' },
    { key: 'actualCenter', header: 'Actual Center' },
    { key: 'scheduledPickup', header: 'Scheduled Pickup' },
    { key: 'scheduledDelivery', header: 'Scheduled Delivery' },
    { key: 'status', header: 'Status' },
    { key: 'gpsLink', header: 'GPS Link' }
];

const toRequest = (opoCase: IOpoCase, statusId: OpoCaseStatus): IOpoCaseRequest => ({
    caseNumber: opoCase.caseNumber,
    unosId: opoCase.unosId,
    originCenterId: opoCase.originCenterId,
    intendedCenterId: opoCase.intendedCenterId,
    actualCenterId: opoCase.actualCenterId,
    scheduledPickupDateTime: opoCase.scheduledPickupDateTime,
    scheduledDeliveryDateTime: opoCase.scheduledDeliveryDateTime,
    actualPickupDateTime: opoCase.actualPickupDateTime,
    actualDeliveryDateTime: opoCase.actualDeliveryDateTime,
    statusId,
    gpsNumber: opoCase.gpsNumber,
    gpsLink: opoCase.gpsLink,
    laterality: opoCase.laterality,
    recipientCenterPocId: opoCase.recipientCenterPocId,
    recipientPocName: '',
    recipientPocEmail: '',
    recipientPocPhone: ''
});

const CaseList = () => {
    const [isPast, setIsPast] = useState(false);
    const [editing, setEditing] = useState<{ show: boolean, opoCase: IOpoCase | null, closing: ICaseClosing | null }>({ show: false, opoCase: null, closing: null });
    const [closing, setClosing] = useState<IOpoCase | null>(null);
    const [deleting, setDeleting] = useState<IOpoCase | null>(null);
    const [actionError, setActionError] = useState('');
    const { cases, centers, recipientCenterPocs, loading, error, save, remove, isCaseNumberAvailable } = useOpoCases(isPast);

    const handleSave = async (request: IOpoCaseRequest) => {
        const result = await save(editing.opoCase ? editing.opoCase.id : null, request);

        if (result.success) {
            setEditing({ show: false, opoCase: null, closing: null });
        }

        return result;
    };

    const handleStatusChange = async (opoCase: IOpoCase, statusId: OpoCaseStatus) => {
        setActionError('');

        const request = statusId == OpoCaseStatus.Pending
            ? { ...toRequest(opoCase, statusId), actualCenterId: null, actualPickupDateTime: null, actualDeliveryDateTime: null }
            : toRequest(opoCase, statusId);
        const result = await save(opoCase.id, request);

        if (!result.success) {
            setActionError(result.message);
        }
    };

    const handleClose = async (caseClosing: ICaseClosing) => {
        const opoCase = closing;
        const hasRequiredFields = opoCase.laterality && opoCase.intendedCenterId && opoCase.recipientCenterPocId;

        setClosing(null);

        if (!caseClosing.wentToIntendedCenter || !hasRequiredFields) {
            setEditing({ show: true, opoCase, closing: caseClosing });
            return;
        }

        setActionError('');

        const result = await save(opoCase.id, {
            ...toRequest(opoCase, OpoCaseStatus.Completed),
            actualCenterId: caseClosing.wentToIntendedCenter ? opoCase.intendedCenterId : null,
            actualPickupDateTime: caseClosing.actualPickupDateTime,
            actualDeliveryDateTime: caseClosing.actualDeliveryDateTime
        });

        if (!result.success) {
            setActionError(result.message);
        }
    };

    const handleDelete = async () => {
        setActionError('');

        const success = await remove(deleting.id);

        setDeleting(null);

        if (!success) {
            setActionError('Unable to delete the case');
        }
    };

    const handleExport = () => {
        const data = cases.map(x => ({
            caseNumber: x.caseNumber,
            unosId: x.unosId,
            originCenter: x.originCenterAlias,
            intendedCenter: x.intendedCenterAlias,
            actualCenter: x.actualCenterAlias,
            scheduledPickup: formatDateTime(x.scheduledPickupDateTime),
            scheduledDelivery: formatDateTime(x.scheduledDeliveryDateTime),
            status: OpoCaseStatusDescription[x.statusId],
            gpsLink: x.gpsLink
        }));

        exportToXlsx(isPast ? 'PastCases' : 'PendingCases', [{ name: 'Cases', data, columns: exportColumns }]);
    };

    return (
        <>
            <CaseFilter
                isPast={isPast}
                caseCount={cases.length}
                loading={loading}
                onIsPastChange={setIsPast}
                onAdd={() => setEditing({ show: true, opoCase: null, closing: null })}
                onExport={handleExport}
            />

            {(error || actionError) && (
                <Alert bsStyle='danger'>{error || actionError}</Alert>
            )}

            {!loading && (
                <CaseTableCard>
                    <Table condensed>
                        <thead>
                            <tr>
                                <th>Case #</th>
                                <th>UNOS ID</th>
                                <th>Origin<br />Center</th>
                                <th>Intended<br />Center</th>
                                <th>Actual<br />Center</th>
                                <th>Scheduled<br />Pickup</th>
                                <th>Scheduled<br />Delivery</th>
                                {isPast && (
                                    <th>Actual<br />Pickup</th>
                                )}
                                {isPast && (
                                    <th>Actual<br />Delivery</th>
                                )}
                                <th>GPS</th>
                                {isPast && (
                                    <th>Went to<br />Intended</th>
                                )}
                                {isPast && (
                                    <th>Status</th>
                                )}
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {cases.map(opoCase =>
                                <CaseRow
                                    key={opoCase.id}
                                    opoCase={opoCase}
                                    showStatus={isPast}
                                    onEdit={x => setEditing({ show: true, opoCase: x, closing: null })}
                                    onClose={setClosing}
                                    onStatusChange={handleStatusChange}
                                    onDelete={setDeleting}
                                />
                            )}

                            {cases.length == 0 && (
                                <tr>
                                    <td colSpan={isPast ? 13 : 9}>{isPast ? 'There are no past cases' : 'There are no pending cases'}</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </CaseTableCard>
            )}

            <CaseEditModal
                show={editing.show}
                opoCase={editing.opoCase}
                closing={editing.closing}
                centers={centers}
                recipientCenterPocs={recipientCenterPocs}
                isCaseNumberAvailable={isCaseNumberAvailable}
                onSave={handleSave}
                onCancel={() => setEditing({ show: false, opoCase: null, closing: null })}
            />

            <CaseCloseModal
                opoCase={closing}
                onConfirm={handleClose}
                onCancel={() => setClosing(null)}
            />

            <CaseDeleteModal
                opoCase={deleting}
                onConfirm={handleDelete}
                onCancel={() => setDeleting(null)}
            />
        </>
    );
}

export default CaseList;
